<script setup lang="ts">
    import { computed, ref } from 'vue';
    import BSDropdownButton from '@shared/components/BSDropdownButton.vue';
    import { bsAlert, AlertTypes } from '@shared/components/bsAlerts';
    import { setUserRole, UserAccountRole, type AccountUser, type RoleSummary } from './AccountUsers.access';

    const props = defineProps<{
        user: AccountUser;
        roles: RoleSummary[];
        disabled: boolean;
    }>();

    const emit = defineEmits<{
        roleChanged: [roleType: UserAccountRole, customRoleId: string | null];
    }>();

    const isBusy = ref(false);

    const currentRoleName = computed(() => {
        if (props.user.UserAccountRole === UserAccountRole.Administrator) return $Resources.UserProfileRoleAdministrator;
        if (props.user.UserAccountRole === UserAccountRole.User) return $Resources.UserProfileRoleUser;

        return props.roles.find((role) => role.RoleId === props.user.CustomRoleId)?.Name ?? '';
    });

    async function setRole(roleType: UserAccountRole, customRoleId: string | null = null): Promise<void> {
        if (isBusy.value) return;

        isBusy.value = true;

        try {
            await setUserRole(props.user.UserId, roleType, customRoleId);
            emit('roleChanged', roleType, customRoleId);
            bsAlert({ type: AlertTypes.Success, title: $Resources.AlertSuccessHeading, content: $Resources.AlertRoleSetBody });
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorHeading, content: $Resources.ErrorLabel });
        } finally {
            isBusy.value = false;
        }
    }
</script>

<template>
    <span v-if="props.disabled" class="text-nowrap">{{ currentRoleName }}</span>

    <BSDropdownButton v-else menuClass="dropdown-menu-right" btnClass="btn-block text-left p-xl-r" :closeOnClickInside="true" :disabled="isBusy">
        <template #toggle>
            {{ currentRoleName }}
            <span class="caret caret-dd"></span>
        </template>
        <template #content>
            <li class="dropdown-header">{{ $Resources.UserProfileRolesAvailableRolesLabel }}:</li>
            <li :class="{ 'active pe-none': props.user.UserAccountRole === UserAccountRole.Administrator }">
                <a href="javascript:void(0)" @click="setRole(UserAccountRole.Administrator)">{{ $Resources.UserProfileRoleAdministrator }}</a>
            </li>
            <li :class="{ 'active pe-none': props.user.UserAccountRole === UserAccountRole.User }">
                <a href="javascript:void(0)" @click="setRole(UserAccountRole.User)">{{ $Resources.UserProfileRoleUser }}</a>
            </li>
            <li
                v-for="role in props.roles"
                :key="role.RoleId"
                :class="{ 'active pe-none': props.user.UserAccountRole === UserAccountRole.Custom && props.user.CustomRoleId === role.RoleId }">
                <a href="javascript:void(0)" @click="setRole(UserAccountRole.Custom, role.RoleId)">{{ role.Name }}</a>
            </li>
        </template>
    </BSDropdownButton>
</template>

